import { Configuration } from "@azure/msal-browser";

export const GMAIL_CONFIG = {
  clientId: import.meta.env.VITE_GMAIL_CLIENT_ID as string,
  clientSecret: import.meta.env.VITE_GMAIL_CLIENT_SECRET as string,
  redirectUri: `${window.location.origin}/settings/email/callback`,
  // SCOPE MINIMIZATION (2026-07-21). Removed: gmail.compose, gmail.modify,
  // gmail.readonly.
  //
  // Why: Google requires apps request scopes only for *implemented* features
  // ("We require that you only request scope access for implemented features" —
  // support.google.com/cloud/answer/13807380). We requested all three and used
  // none: the only Gmail API call in the codebase is users/me/messages/send
  // (useEmailProvider.ts, useEmailSender.ts, email-queue-processor sendViaGmail).
  //
  // Why it matters: gmail.readonly and gmail.modify are RESTRICTED scopes, which
  // trigger annual third-party CASA security assessment when restricted data is
  // stored or transmitted. gmail.send is merely "sensitive" — a lower tier with
  // no assessment. Carrying unused restricted scopes was pure liability.
  //
  // Verified safe: all 3 connected Gmail accounts were dormant at removal time
  // (0 sends in 90 days, 0 token-refresh failures, 2 of 3 never assigned a single
  // queue job). 100% of live sending is Outlook.
  //
  // BEFORE RE-ADDING ANY OF THESE: they are the wrong tool for bounce detection.
  // See docs/superpowers/specs/2026-07-21-hermes-deliverability-design.md §6.3 —
  // the chosen design polls Outlook via Graph Mail.Read (no CASA equivalent) and,
  // if Gmail bounce handling is ever needed, uses customer-configured forwarding
  // rather than a mailbox read. Re-adding a restricted scope re-arms CASA.
  //
  // NOT removed, both are load-bearing — do not "minimize" these:
  //   - calendar (full): calendar.events does NOT grant calendarList, which is
  //     used by CalendarCallback.tsx, EmailCallback.tsx, calendar-sync/index.ts.
  //   - Microsoft Mail.ReadWrite (see MICROSOFT_SCOPES below): powers the entire
  //     Outlook attachment/inline-image path in useEmailSender.ts (draft create,
  //     attachments, send). Mail.Send alone cannot do it.
  //
  // Existing grants are unaffected — connected accounts keep what they already
  // consented to until they reconnect. Deliberate: no forced re-consent. The
  // OAuth consent screen in Google Cloud Console must be updated separately.
  scope: [
    'https://www.googleapis.com/auth/gmail.send',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/calendar',
    'https://www.googleapis.com/auth/calendar.events',
  ].join(' '),
};

export const MICROSOFT_CONFIG: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID as string,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common'}`,
    redirectUri: `${window.location.origin}/settings/outlook/callback`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const MICROSOFT_SCOPES = [
  'openid',
  'profile',
  'email',
  'Mail.Send',
  'Mail.ReadWrite',
  'Calendars.ReadWrite',
  'offline_access',
  'User.Read',
];

// --- Outlook Web (confidential authorization-code) flow ---
// New redirect URI (registered as a "Web" platform in Entra). Distinct from
// MICROSOFT_CONFIG.redirectUri (the SPA path), which stays live during migration.
export const MICROSOFT_WEB_REDIRECT_URI = `${window.location.origin}/settings/outlook/connected`;

// Space-delimited scope string for the /authorize URL. Mirrors MICROSOFT_SCOPES
// (which MSAL consumes as an array) plus offline_access for the refresh token.
export const MICROSOFT_AUTHORIZE_SCOPES = MICROSOFT_SCOPES.join(' ');